import { useMemo } from 'react';
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HiXMark as CloseIcon } from 'react-icons/hi2';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, useToast } from '@knack/asterisk-react';
import { type Cell } from '@tanstack/react-table';
import { isAxiosError } from 'axios';
import { type z } from 'zod';

import { type FormViewFieldInput } from '@/types/schema/views/FormView';
import { type TableView } from '@/types/schema/views/TableView';
import { useUpdateRecordMutation } from '@/hooks/api/mutations/useUpdateRecordMutation';
import { type ViewRecord } from '@/hooks/api/queries/useViewRecordQuery';
import { knackDatePickerChildFormDataTransformer } from '@/components/knack-date-picker/knackDatePickerChildFormDataTransformer';
import { type ViewWithTableColumn } from '@/components/views/common/table/types';
import { FormFieldInput } from '@/components/views/form/FormFieldInput';
import { getDynamicFormSchema } from '@/components/views/form/schema/helper';
import { getDefaultValueFromDateTimeField } from '@/components/views/form/utils';
import { useViewContext } from '@/components/views/ViewContext';

interface EditableCellFormProps {
  cell: Cell<ViewRecord, unknown>;
  input: FormViewFieldInput;
  viewColumn: ViewWithTableColumn;
  handleClosePopover: () => void;
}

export function EditableCellForm({
  cell,
  input,
  viewColumn,
  handleClosePopover
}: EditableCellFormProps) {
  const [t] = useTranslation();
  const { sourceTable, view } = useViewContext<TableView>();
  const { presentToast } = useToast();

  const { mutate: updateRecord, isPending } = useUpdateRecordMutation({ viewKey: view.key });

  const recordValues = cell.row.original;
  const recordRawValues = cell.getContext().table.options.meta?.rawValues[
    cell.row.index
  ] as ViewRecord;

  const formSchema = getDynamicFormSchema({
    inputs: [input],
    sourceTableFields: sourceTable.fields
  });

  type FormSchemaType = z.infer<typeof formSchema>;

  const fieldDefaultValue = useMemo(() => {
    const rawValue = recordRawValues[viewColumn.field.key];
    const field = sourceTable.fields.find((f) => f.key === viewColumn.field.key);

    if (!field) {
      return rawValue;
    }

    if (field.type === 'date_time') {
      const dateTimeValue = rawValue || getDefaultValueFromDateTimeField(field.format);
      return knackDatePickerChildFormDataTransformer.fromServer(dateTimeValue, field.format);
    }

    return rawValue;
  }, [recordRawValues, viewColumn.field.key, sourceTable.fields]);

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: { [input.field.key]: fieldDefaultValue }
  });

  const onSubmit: SubmitHandler<FormSchemaType> = (data) => {
    // Sanitize the data before sending it to the server
    // We need to transform the date_time fields to match the expected format from the server
    const sanitizedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        const field = sourceTable.fields.find((f) => f.key === key);
        if (!field) {
          return [key, value];
        }

        if (field.type === 'date_time') {
          return [key, knackDatePickerChildFormDataTransformer.toServer(value, field.format)];
        }

        // 🚨 Note: The input value does not include a `times` array, but the API requires it for saving.
        // This differs from the form behavior, which both receives and submits timer data without a `times` array.
        if (field.type === 'timer') {
          // Ensure the value is wrapped in a `times` array as expected by the API.
          // If `times` is missing, the server may return a 500 error instead of validation errors.
          return [key, { times: [value || {}] }];
        }
        return [key, value];
      })
    );

    updateRecord(
      {
        data: sanitizedData,
        recordId: recordValues.id
      },
      {
        onSuccess: () => {
          presentToast({
            intent: 'success',
            title: t('components.views.table.record_updated', { fieldName: viewColumn.header })
          });
          handleClosePopover();
        },
        onError: (error: Error) => {
          if (isAxiosError(error) && error.response) {
            const { field, message } = error.response.data.errors[0];
            form.setError(field, {
              message,
              type: 'schema'
            });
          }
        }
      }
    );
  };

  return (
    <FormProvider {...form}>
      <div className="flex items-center justify-between">
        <span className="font-semibold">
          {t('actions.edit')} {viewColumn.header}
        </span>
        <Button intent="minimal" size="sm" onClick={handleClosePopover} tabIndex={-1}>
          <CloseIcon size={14} className="shrink-0" />
        </Button>
      </div>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="mb-3">
          <FormFieldInput input={input} isInlineEdit />
        </div>
        <div className="flex justify-end gap-2">
          <Button intent="minimal" size="sm" onClick={handleClosePopover}>
            {t('actions.cancel')}
          </Button>
          <Button intent="primary" size="sm" type="submit" disabled={isPending}>
            {isPending ? t('actions.saving') : t('actions.save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
